import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { X, HelpCircle, ChevronRight, Lightbulb, ListChecks } from 'lucide-react'

// =============================================================================
// Types
// =============================================================================

interface HelpContent {
  title: string
  description: string
  steps?: string[]
  tips?: string[]
}

// =============================================================================
// Route to Help Key Mapping
// =============================================================================

const routeToHelpKey: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/sales/contracts': 'contracts',
  '/sales/contracts/new': 'contractGenerator',
  '/sales/properties': 'properties',
  '/admin/companies': 'organizations',
  '/admin/contacts': 'contacts',
  '/admin/settings': 'settings',
  '/admin/settings/users': 'users',
  '/admin/settings/signatories': 'signatories',
}

// =============================================================================
// HelpSidebar Component
// =============================================================================

interface HelpSidebarProps {
  isOpen: boolean
  onClose: () => void
}

export default function HelpSidebar({ isOpen, onClose }: HelpSidebarProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const [helpKey, setHelpKey] = useState<string>('dashboard')

  // Determine help key based on current route
  useEffect(() => {
    const path = location.pathname
    // Find matching route (exact or prefix match)
    const matchedKey = routeToHelpKey[path] ||
      Object.entries(routeToHelpKey).find(([route]) => path.startsWith(route))?.[1] ||
      'dashboard'
    setHelpKey(matchedKey)
  }, [location.pathname])

  // Get help content for current page
  const getHelpContent = (): HelpContent => {
    const title = t(`help.${helpKey}.title`, { defaultValue: '' })
    const description = t(`help.${helpKey}.description`, { defaultValue: '' })

    // Get steps array
    const stepsRaw = t(`help.${helpKey}.steps`, { returnObjects: true })
    const steps = Array.isArray(stepsRaw) ? stepsRaw : []

    // Get tips array
    const tipsRaw = t(`help.${helpKey}.tips`, { returnObjects: true })
    const tips = Array.isArray(tipsRaw) ? tipsRaw : []

    return { title, description, steps, tips }
  }

  const helpContent = getHelpContent()

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40 lg:hidden"
        onClick={onClose}
      />

      {/* Sidebar */}
      <aside className={`
        fixed top-0 right-0 h-full w-80 max-w-[calc(100vw-1rem)] bg-white shadow-2xl z-50
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700">
          <div className="flex items-center gap-2 text-white">
            <HelpCircle size={24} />
            <h2 className="text-lg font-semibold">{t('help.title')}</h2>
          </div>
          <button
            data-testid="help-sidebar-close-btn"
            onClick={onClose}
            className="p-1 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100%-64px)] p-4">
          {/* Page Title */}
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {helpContent.title}
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              {helpContent.description}
            </p>
          </div>

          {/* Steps */}
          {helpContent.steps && helpContent.steps.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <ListChecks size={18} className="text-blue-600" />
                <h4 className="font-semibold text-gray-900">Schritte</h4>
              </div>
              <ol className="space-y-2">
                {helpContent.steps.map((step, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-sm text-gray-700"
                  >
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-medium">
                      {index + 1}
                    </span>
                    <span className="pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Tips */}
          {helpContent.tips && helpContent.tips.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb size={18} className="text-amber-500" />
                <h4 className="font-semibold text-gray-900">Tipps</h4>
              </div>
              <ul className="space-y-2">
                {helpContent.tips.map((tip, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-sm text-gray-700 bg-amber-50 p-3 rounded-lg"
                  >
                    <ChevronRight size={16} className="flex-shrink-0 text-amber-500 mt-0.5" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Immotron v{__APP_VERSION__}{__BUILD_NUMBER__ !== 'dev' ? `+${__BUILD_NUMBER__}` : ''}
            </p>
          </div>
        </div>
      </aside>
    </>
  )
}
